import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Navigation } from "lucide-react";
import { CITIES, COMPANY } from "@/lib/data";

// Real lat/lon coordinates for HQ + service cities
const HQ = {
  lat: 38.8304,
  lng: -77.1964,
  label: "Comfort Care HQ",
  address: "4209 Evergreen Lane, Suite #202, Annandale, VA 22003",
};

const CITY_COORDS = {
  // annandale is intentionally omitted — the HQ heart pin already marks Annandale.
  arlington:        { lat: 38.8816, lng: -77.0910 },
  alexandria:       { lat: 38.8048, lng: -77.0469 },
  fairfax:          { lat: 38.8462, lng: -77.3064 },
  "washington-dc":  { lat: 38.9072, lng: -77.0369 },
  bethesda:         { lat: 38.9847, lng: -77.0947 },
  "silver-spring":  { lat: 38.9907, lng: -77.0261 },
};

export default function ServiceMap() {
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;

    // Inject Leaflet CSS once
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    import("leaflet").then((LMod) => {
      if (cancelled || !containerRef.current) return;
      const L = LMod.default || LMod;

      // Initialize map
      const map = L.map(containerRef.current, {
        zoomControl: false,
        scrollWheelZoom: false,
        attributionControl: false,
        dragging: true,
        doubleClickZoom: true,
        touchZoom: true,
      }).setView([37.9, -77.5], 7);

      mapRef.current = map;

      // Clean Apple-Maps-style tiles (CartoDB Voyager — light, crisp)
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {
          subdomains: "abcd",
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap &copy; CARTO',
        }
      ).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      // Custom HQ icon (brand teal heart-pin)
      const hqIcon = L.divIcon({
        className: "cc-hq-marker",
        html: `
          <div style="position:relative;width:48px;height:48px;display:flex;align-items:center;justify-content:center;">
            <div style="position:absolute;inset:0;border-radius:9999px;background:rgba(0,139,155,0.25);animation:cc-pulse 2s ease-out infinite;"></div>
            <div style="position:relative;width:36px;height:36px;border-radius:9999px;background:#008B9B;border:3px solid #fff;box-shadow:0 6px 20px -4px rgba(0,139,155,0.7);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px;">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            </div>
          </div>
        `,
        iconSize: [48, 48],
        iconAnchor: [24, 24],
      });

      L.marker([HQ.lat, HQ.lng], { icon: hqIcon, zIndexOffset: 1000 })
        .addTo(map)
        .bindPopup(
          `<div style="font-family:'Outfit',sans-serif;min-width:220px;">
            <div style="display:inline-block;background:#008B9B;color:#fff;font-size:9px;font-weight:700;letter-spacing:2px;padding:2px 8px;border-radius:9999px;text-transform:uppercase;margin-bottom:6px;">Main Hub</div>
            <div style="font-weight:700;color:#0A192F;font-size:15px;margin-bottom:4px;">${HQ.label} — Annandale, VA</div>
            <div style="color:#4A5568;font-size:12px;line-height:1.5;margin-bottom:8px;">${HQ.address}</div>
            <a href="tel:${COMPANY.phoneRaw}" style="color:#008B9B;font-weight:600;text-decoration:none;font-size:13px;display:block;margin-bottom:6px;">📞 ${COMPANY.phone}</a>
            <a href="/service-area/annandale" style="color:#008B9B;font-weight:600;text-decoration:none;font-size:12px;">Explore Annandale NEMT →</a>
          </div>`,
          { closeButton: true, className: "cc-popup" }
        );

      // City service markers
      const cityIcon = L.divIcon({
        className: "cc-city-marker",
        html: `
          <div style="position:relative;width:18px;height:18px;">
            <div style="position:absolute;inset:0;border-radius:9999px;background:rgba(0,139,155,0.35);animation:cc-pulse 2.2s ease-out infinite;"></div>
            <div style="position:relative;width:14px;height:14px;margin:2px;border-radius:9999px;background:#008B9B;border:2.5px solid #fff;box-shadow:0 4px 12px -2px rgba(10,25,47,0.4);"></div>
          </div>
        `,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      CITIES.forEach((c) => {
        const coords = CITY_COORDS[c.slug];
        if (!coords) return;
        const marker = L.marker([coords.lat, coords.lng], { icon: cityIcon }).addTo(map);
        marker.bindPopup(
          `<div style="font-family:'Outfit',sans-serif;min-width:180px;">
            <div style="font-weight:700;color:#0A192F;font-size:14px;">${c.name}, ${c.region === "Maryland" ? "MD" : c.region === "District of Columbia" ? "DC" : "VA"}</div>
            <div style="color:#4A5568;font-size:12px;margin:4px 0 8px;">${c.blurb}</div>
            <a href="/service-area/${c.slug}" style="color:#008B9B;font-weight:600;text-decoration:none;font-size:12px;">Explore ${c.name} →</a>
          </div>`,
          { className: "cc-popup" }
        );

        // Cities in the dense DC/NoVA cluster: show label only on hover (avoid overlap).
        // Spaced-out cities: show permanent label for visual clarity.
        const denseCluster = [
          "arlington", "alexandria", "fairfax", "washington-dc", "bethesda", "silver-spring",
        ].includes(c.slug);

        marker.bindTooltip(c.name, {
          permanent: !denseCluster,
          direction: "right",
          offset: [10, 0],
          className: "cc-city-tooltip",
        });
      });

      // Fit bounds to show HQ + all cities with padding
      const allLatLngs = [
        [HQ.lat, HQ.lng],
        ...CITIES
          .filter((c) => CITY_COORDS[c.slug])
          .map((c) => [CITY_COORDS[c.slug].lat, CITY_COORDS[c.slug].lng]),
      ];
      map.fitBounds(allLatLngs, { padding: [40, 40] });

      cleanup = () => {
        map.remove();
        mapRef.current = null;
      };
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  return (
    <div className="relative rounded-3xl overflow-hidden border border-gray-100 bg-white p-4 md:p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-teal-600 font-medium">Interactive Service Map</div>
          <div className="font-heading text-lg font-semibold text-navy">Virginia coverage at a glance</div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-navy/60">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-500 ring-4 ring-teal-500/20" />
          Service city
        </div>
      </div>

      <div className="relative">
        <div
          ref={containerRef}
          data-testid="leaflet-map"
          className="w-full aspect-[16/10] md:aspect-[5/4] lg:aspect-[16/10] rounded-2xl overflow-hidden bg-sand"
          style={{ minHeight: "360px" }}
        />

        {/* HQ Address overlay card */}
        <div className="absolute bottom-3 left-3 right-3 sm:right-auto sm:max-w-xs bg-white/95 backdrop-blur rounded-2xl p-3.5 shadow-xl border border-gray-100 z-[400]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center shrink-0">
              <Navigation className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-teal-600 font-semibold">Headquarters</div>
              <div className="font-heading font-semibold text-navy text-sm leading-tight mt-0.5">
                4209 Evergreen Lane, Suite #202
              </div>
              <div className="text-xs text-navy/60">Annandale, VA 22003</div>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-700 font-semibold mt-1.5"
              >
                <Phone className="w-3 h-3" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>

        <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[10px] text-navy bg-white/90 backdrop-blur px-2.5 py-1 rounded-full border border-gray-100 shadow-sm z-[400]">
          <MapPin className="w-3 h-3 text-teal-500" />
          {CITIES.length}+ cities served
        </div>
      </div>

      <p className="text-xs text-navy/55 text-center mt-3">
        Tap any pin to view service details and call our team directly.
      </p>

      {/* Leaflet style overrides */}
      <style>{`
        @keyframes cc-pulse {
          0%   { transform: scale(1);   opacity: 0.7; }
          80%  { transform: scale(2.4); opacity: 0;   }
          100% { transform: scale(2.4); opacity: 0;   }
        }
        .cc-city-tooltip {
          background: rgba(255,255,255,0.95) !important;
          color: #0A192F !important;
          border: 1px solid rgba(10,25,47,0.08) !important;
          border-radius: 9999px !important;
          padding: 2px 8px !important;
          font-size: 10px !important;
          font-weight: 600 !important;
          font-family: 'Outfit', sans-serif !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
        }
        .cc-city-tooltip::before { display:none !important; }
        .cc-popup .leaflet-popup-content-wrapper {
          border-radius: 16px !important;
          box-shadow: 0 10px 40px -10px rgba(10,25,47,0.25) !important;
        }
        .cc-popup .leaflet-popup-tip { box-shadow: none !important; }
        .leaflet-container { font-family: 'DM Sans', sans-serif; }
        .leaflet-control-zoom a {
          background: #fff !important;
          color: #0A192F !important;
          border: 1px solid rgba(10,25,47,0.08) !important;
          border-radius: 8px !important;
          margin-bottom: 4px !important;
        }
        .leaflet-control-zoom a:hover {
          background: #008B9B !important;
          color: #fff !important;
        }
      `}</style>
    </div>
  );
}
